/**
 * Create and edit an activity, and move it through its lifecycle.
 *
 * The rule this dialog exists to make honest: **DONE requires a result**. The
 * server enforces it, and so does this form — not because the server needs
 * help, but because a rep who clicks «انجام شد» and gets a red banner has
 * already lost the thought they were about to write down. The field appears
 * first and the button waits.
 *
 * Reopening a finished activity is an ADMIN act that owes a reason, and the
 * control is simply absent for a USER — the same shape as every other
 * ADMIN-only affordance in this app (hidden here, refused at the BFF, refused
 * again at the service).
 */
import { useMutation, useQuery } from '@apollo/client';
import { Paperclip, Plus, Trash2, X } from 'lucide-react';
import React, { useEffect, useMemo, useState } from 'react';
import { Button } from '@meshhub/design-tokens/ui';
import {
  CheckboxField,
  ConfirmDialog,
  FormDialog,
  JalaliDateField,
  MutationError,
  SelectField,
  TextArea,
  TextField,
  TimeField,
} from '../FormKit.web.jsx';
import {
  ACTIVITY_STATUS_LABELS,
  REMINDER_CHANNEL_LABELS,
  formatDateTime,
  isoDateAndTimeToInstant,
  isoToDateAndTime,
} from '../../lib/format.js';
import {
  ACTIVITY_ATTACHMENTS_QUERY,
  ACTIVITY_TYPES_QUERY,
  CHANGE_ACTIVITY_STATUS,
  CONTACTS_QUERY,
  CREATE_ACTIVITY,
  CREATE_ACTIVITY_TYPE,
  DELETE_ACTIVITY,
  SET_REMINDERS,
  UPDATE_ACTIVITY,
  USERS_QUERY,
} from '../../lib/queries.js';
import { formatBytes, formatCount } from '../../lib/format.js';
import { openAttachment, uploadFile } from '../../lib/files.js';
import { S } from '../../lib/strings.js';
import { useSession } from '../../lib/session.jsx';
import { ChecklistPanel } from './ChecklistPanel.web.jsx';
import { VocabularyField } from '../VocabularyField.web.jsx';

/** Reminder offsets a person actually picks, in minutes before the start. */
const OFFSETS = [10, 30, 60, 1440];

/**
 * How many contacts the picker lists before search is the only way through.
 *
 * Matches the affiliation and referrer pickers, which have always used 25. It
 * was 10 here, and a plain `<select>` gives no hint that a page even exists —
 * so five of fifteen contacts were simply unreachable.
 */
const CONTACT_PICKER_SIZE = 25;

const emptyForm = (seed = {}) => ({
  typeId: '',
  title: '',
  contactId: '',
  description: '',
  allDay: false,
  date: '',
  startTime: '09:00',
  endTime: '10:00',
  // Who is responsible for DOING this. Empty means the caller, which is what
  // the scheduling service reads an empty `assigned_to` as — so a USER, who
  // may not assign to anybody else, sends nothing and gets themselves.
  //
  // It used to be absent from this object entirely while a text box on the
  // form bound to `form.assignedTo`, so the box wrote to a key nothing read
  // and `submit()` never sent it. Typing in it did nothing at all.
  assignedTo: '',
  reminders: [],
  ...seed,
});

/** An activity (or a drag range, or a suggestion prefill) -> form state. */
function toForm(activity, prefill) {
  if (activity) {
    const start = isoToDateAndTime(activity.startsAt);
    const end = activity.endsAt ? isoToDateAndTime(activity.endsAt) : null;
    return emptyForm({
      typeId: activity.type?.id ?? '',
      title: activity.title ?? '',
      contactId: activity.contactId ?? '',
      description: activity.description ?? '',
      allDay: activity.allDay ?? false,
      assignedTo: activity.assignedTo ?? '',
      date: start.date,
      startTime: start.time,
      endTime: end?.time ?? '',
      reminders: (activity.reminders ?? [])
        .filter((r) => !r.firedAt)
        .map((r) => ({
          offset: Math.round(
            (new Date(activity.startsAt) - new Date(r.remindAt)) / 60_000,
          ),
          channel: r.channel,
        })),
    });
  }
  if (prefill?.startsAt) {
    const start = isoToDateAndTime(prefill.startsAt.toISOString?.() ?? prefill.startsAt);
    const end = prefill.endsAt
      ? isoToDateAndTime(prefill.endsAt.toISOString?.() ?? prefill.endsAt)
      : null;
    return emptyForm({
      date: start.date,
      startTime: start.time,
      endTime: end?.time ?? '',
      contactId: prefill.contactId ?? '',
      title: prefill.title ?? '',
      typeId: prefill.typeId ?? '',
    });
  }
  return emptyForm(prefill ?? {});
}

/**
 * @param onCreate  optional. When given, the dialog hands the assembled input to
 *   it INSTEAD of running `createActivity` itself. The follow-up inbox needs
 *   this: accepting a suggestion must go through `acceptSuggestion`, which
 *   creates the activity and resolves the suggestion in ONE transaction. Letting
 *   the dialog create it too would produce two activities — one linked, one
 *   stray.
 */
export function ActivityDialog({ open, onOpenChange, activity, prefill, onSaved, onCreate }) {
  const { isAdmin, me } = useSession();
  const [form, setForm] = useState(() => toForm(activity, prefill));
  const [resultNote, setResultNote] = useState('');
  const [reopenReason, setReopenReason] = useState('');
  const [showResult, setShowResult] = useState(false);
  const [showReopen, setShowReopen] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [contactSearch, setContactSearch] = useState('');
  // Files chosen but not yet uploaded. Same shape and same flow as the
  // interaction composer's, because it is the same three-step protocol.
  const [files, setFiles] = useState([]);
  const [uploadError, setUploadError] = useState(null);
  const fileInputRef = React.useRef(null);
  const uploading = files.some((f) => f.state === 'uploading');

  const addFiles = (list) => {
    // MATERIALISED HERE, not inside the updater. `list` is the input's live
    // `FileList`, and the handler resets `input.value` immediately so choosing
    // the same file twice fires another change event — which EMPTIES the
    // FileList. React runs the updater afterwards, so spreading `list` in
    // there produced an empty array and the file silently vanished between
    // being chosen and being listed.
    const chosen = [...list].map((file) => ({
      key: `${file.name}:${file.size}:${file.lastModified}`,
      file,
      state: 'queued',
      progress: 0,
      error: null,
    }));
    setFiles((current) => {
      const known = new Set(current.map((f) => f.key));
      return [...current, ...chosen.filter((f) => !known.has(f.key))];
    });
  };

  const patchFile = (key, patch) =>
    setFiles((current) => current.map((f) => (f.key === key ? { ...f, ...patch } : f)));

  const isEdit = Boolean(activity?.id);
  const terminal = activity?.status === 'DONE' || activity?.status === 'CANCELLED';

  useEffect(() => {
    if (!open) return;
    setForm(toForm(activity, prefill));
    setResultNote('');
    setReopenReason('');
    setShowResult(false);
    setShowReopen(false);
    setContactSearch('');
    setFiles([]);
    setUploadError(null);
  }, [open, activity, prefill]);

  const { data: typeData, loading: typesLoading } = useQuery(ACTIVITY_TYPES_QUERY, {
    skip: !open,
  });
  const types = useMemo(() => typeData?.activityTypes ?? [], [typeData]);

  // A DEPLOYMENT WITH NO ACTIVITY TYPES CANNOT BOOK ANYTHING, and until this
  // existed the only ways to add one were a seed script with database access or
  // a hand-written mutation. `awaitRefetchQueries` matters: the picker selects
  // what it just created, and the option has to be in the list first.
  const [createType, createTypeState] = useMutation(CREATE_ACTIVITY_TYPE, {
    refetchQueries: ['ActivityTypes'],
    awaitRefetchQueries: true,
  });

  // The default type is the first ACTIVE one in the deployment's own ordering.
  // No code names a type — that would break product-neutrality the moment
  // somebody adopted MeshHub with a different vocabulary.
  useEffect(() => {
    if (!form.typeId && types.length > 0) {
      setForm((current) => ({ ...current, typeId: types[0].id }));
    }
  }, [types, form.typeId]);

  // WHY THE PAGE SIZE MATTERS HERE. This is a `<select>`, not an infinite
  // list: whatever the first page holds is the whole world as far as the user
  // can tell, and there is no "load more" to reveal the rest. At ten, a
  // deployment with fifteen contacts showed ten and silently hid five — the
  // reported bug. It is the *same* CONTACTS_QUERY the affiliation and referrer
  // pickers use, and both of those already asked for 25.
  //
  // `totalCount` is selected so the form can SAY when the list is truncated,
  // rather than leaving the user to wonder. Searching narrows server-side and
  // is the intended path past the cap.
  const { data: contactData } = useQuery(CONTACTS_QUERY, {
    variables: { page: 1, pageSize: CONTACT_PICKER_SIZE, search: contactSearch || null },
    skip: !open,
  });
  const contacts = contactData?.contacts?.items ?? [];
  const contactTotal = contactData?.contacts?.totalCount ?? 0;
  const hiddenContacts = Math.max(0, contactTotal - contacts.length);

  // The SAME query the calendar's ADMIN user switcher runs; Apollo dedupes
  // them when both are mounted. Skipped for a USER, who may not assign to
  // anybody else and would be refused by the ADMIN-only `users` field anyway.
  const { data: userData } = useQuery(USERS_QUERY, {
    variables: { page: 1, pageSize: 100 },
    skip: !open || !isAdmin,
    errorPolicy: 'ignore',
  });
  const users = userData?.users?.items ?? [];

  // Only when editing: a new activity has no id and therefore no files, and
  // the calendar must not pay for this on every activity it renders.
  const { data: fileData, refetch: refetchFiles } = useQuery(ACTIVITY_ATTACHMENTS_QUERY, {
    variables: { id: activity?.id },
    skip: !open || !activity?.id,
    fetchPolicy: 'cache-and-network',
  });
  const stored = fileData?.activity?.attachments ?? [];

  const [createActivity, createState] = useMutation(CREATE_ACTIVITY);
  const [updateActivity, updateState] = useMutation(UPDATE_ACTIVITY);
  const [changeStatus, statusState] = useMutation(CHANGE_ACTIVITY_STATUS);
  const [deleteActivity] = useMutation(DELETE_ACTIVITY);
  const [setReminders] = useMutation(SET_REMINDERS);

  const busy =
    createState.loading || updateState.loading || statusState.loading || uploading;
  const error =
    createState.error ?? updateState.error ?? statusState.error ?? uploadError;

  const startsAt = isoDateAndTimeToInstant(form.date, form.allDay ? '00:00' : form.startTime);
  const endsAt = form.allDay
    ? null
    : form.endTime
      ? isoDateAndTimeToInstant(form.date, form.endTime)
      : null;

  const set = (field) => (event) => {
    const value =
      event?.target?.type === 'checkbox' ? event.target.checked : event?.target?.value ?? event;
    setForm((current) => ({ ...current, [field]: value }));
  };

  function reminderInputs() {
    if (!startsAt) return [];
    return form.reminders.map((r) => ({
      remindAt: new Date(new Date(startsAt).getTime() - r.offset * 60_000).toISOString(),
      channel: r.channel,
    }));
  }

  /**
   * Upload one queued file against an activity that now exists.
   *
   * `ownerType: 'ACTIVITY'` goes to the CRM's attachment store, which is where
   * every other attachment in MeshHub lives; the scheduling service holds no
   * files at all. Same `/files/initiate → PUT → /files/complete` flow, same
   * `lib/files.js`, no second implementation.
   */
  async function uploadOne(entry, activityId) {
    patchFile(entry.key, { state: 'uploading', progress: 0, error: null });
    try {
      await uploadFile({
        file: entry.file,
        ownerType: 'ACTIVITY',
        ownerId: activityId,
        onProgress: (fraction) => patchFile(entry.key, { progress: fraction }),
      });
      patchFile(entry.key, { state: 'done', progress: 1 });
      return true;
    } catch (err) {
      patchFile(entry.key, { state: 'failed', error: err.message });
      return false;
    }
  }

  async function submit() {
    if (!startsAt) return;
    const input = {
      typeId: form.typeId,
      title: form.title,
      startsAt,
      endsAt,
      allDay: form.allDay,
      contactId: form.contactId || null,
      description: form.description || null,
      // Omitted means "the caller" at the service, so an unset picker is not
      // the same as sending an empty id — send the key only when there is one.
      ...(form.assignedTo ? { assignedTo: form.assignedTo } : {}),
    };

    let activityId = activity?.id ?? null;
    if (isEdit) {
      await updateActivity({ variables: { id: activity.id, input } });
      await setReminders({
        variables: { activityId: activity.id, reminders: reminderInputs() },
      });
    } else if (onCreate) {
      // The follow-up inbox creates through `acceptSuggestion`, which returns
      // the suggestion rather than the activity — so there is no id to attach
      // to here, and the file picker is hidden on that path.
      await onCreate({ ...input, reminders: reminderInputs() });
    } else {
      const created = await createActivity({
        variables: { input: { ...input, reminders: reminderInputs() } },
      });
      activityId = created.data?.createActivity?.id ?? null;
    }

    // AFTER the activity exists, because an attachment needs an owner id — the
    // same ordering, and the same one-at-a-time pacing, as the interaction
    // composer: a congress-hall connection does better sequentially and the
    // progress bars stay legible.
    const pending = files.filter((f) => f.state !== 'done');
    let allUploaded = true;
    if (activityId) {
      for (const entry of pending) {
        allUploaded = (await uploadOne(entry, activityId)) && allUploaded;
      }
      // ONLY when editing. `useQuery` hands back a `refetch` even while
      // `skip` is true, and calling it on a create — where there is no
      // `activity.id` yet — fires the query with no variables and throws
      // «Variable "$id" of required type "ID!" was not provided», which
      // aborted `submit` and left the dialog open on a successful save.
      // A freshly created activity's list is the files just uploaded anyway.
      if (pending.length > 0 && activity?.id) await refetchFiles();
    }

    onSaved?.(activityId);
    if (!allUploaded) {
      // The activity SAVED; only some files did not. Keeping the dialog open
      // lets those be retried without retyping anything.
      setUploadError(new Error(S.activity.uploadFailed));
      return;
    }
    onOpenChange(false);
  }

  async function applyStatus(toStatus, extra = {}) {
    await changeStatus({ variables: { id: activity.id, toStatus, ...extra } });
    onSaved?.(activity.id);
    onOpenChange(false);
  }

  const statusActions = !isEdit ? null : (
    <div className="flex flex-col gap-3 rounded border border-border bg-bg p-3">
      <div className="flex items-center justify-between">
        <span className="text-sm font-medium">
          {ACTIVITY_STATUS_LABELS[activity.status] ?? activity.status}
        </span>
        {activity.resultedAt && (
          <span className="text-xs text-text-muted">{formatDateTime(activity.resultedAt)}</span>
        )}
      </div>

      {activity.resultNote && (
        <p className="whitespace-pre-wrap text-sm text-text-muted">{activity.resultNote}</p>
      )}

      {!terminal && (
        <div className="flex flex-wrap gap-2">
          {/* DONE is a two-step: the note first, then the button. A rep who
              clicks and is refused has already lost the thought. */}
          {!showResult ? (
            <Button type="button" variant="primary" onClick={() => setShowResult(true)}>
              {S.activity.markDone}
            </Button>
          ) : null}
          <Button type="button" onClick={() => applyStatus('CANCELLED')}>
            {S.activity.markCancelled}
          </Button>
          {activity.status === 'SCHEDULED' && (
            <Button type="button" onClick={() => applyStatus('MISSED')}>
              {S.activity.markMissed}
            </Button>
          )}
        </div>
      )}

      {showResult && (
        <div className="flex flex-col gap-2">
          <TextArea
            label={S.activity.result}
            hint={S.activity.resultRequired}
            rows={3}
            value={resultNote}
            onChange={(event) => setResultNote(event.target.value)}
          />
          <div className="flex gap-2">
            <Button
              type="button"
              variant="primary"
              disabled={!resultNote.trim() || busy}
              onClick={() => applyStatus('DONE', { resultNote })}
            >
              {S.activity.markDone}
            </Button>
            <Button type="button" onClick={() => setShowResult(false)}>
              {S.app.cancel}
            </Button>
          </div>
        </div>
      )}

      {/* Reopening is ADMIN-only and owes a reason. Absent for a USER; the BFF
          and the service refuse it independently. */}
      {terminal && isAdmin && !showReopen && (
        <Button type="button" onClick={() => setShowReopen(true)}>
          {S.activity.reopen}
        </Button>
      )}
      {terminal && isAdmin && showReopen && (
        <div className="flex flex-col gap-2">
          <TextField
            label={S.activity.reopenReason}
            hint={S.activity.reopenReasonHint}
            value={reopenReason}
            onChange={(event) => setReopenReason(event.target.value)}
          />
          <div className="flex gap-2">
            <Button
              type="button"
              variant="primary"
              disabled={!reopenReason.trim() || busy}
              onClick={() => applyStatus('SCHEDULED', { reason: reopenReason })}
            >
              {S.activity.reopen}
            </Button>
            <Button type="button" onClick={() => setShowReopen(false)}>
              {S.app.cancel}
            </Button>
          </div>
        </div>
      )}
    </div>
  );

  return (
    <>
      <FormDialog
        open={open}
        onOpenChange={onOpenChange}
        wide
        title={isEdit ? S.activity.edit : S.activity.create}
        submitLabel={isEdit ? S.app.save : S.activity.create}
        busy={busy}
        onSubmit={submit}
        footer={
          isEdit ? (
            <Button type="button" variant="ghost" onClick={() => setConfirmDelete(true)}>
              <Trash2 size={14} aria-hidden="true" />
              {S.activity.delete}
            </Button>
          ) : null
        }
      >
        <MutationError error={error} />

        {statusActions}

        <VocabularyField
          label={S.activity.type}
          value={form.typeId}
          onChange={(id) => setForm((current) => ({ ...current, typeId: id }))}
          options={types}
          loading={typesLoading}
          required
          canCreate={isAdmin}
          codePrefix="ACTIVITY"
          creating={createTypeState.loading}
          createError={createTypeState.error}
          onCreate={async (input) => {
            const created = await createType({ variables: { input } });
            return created.data?.createActivityType;
          }}
        />

        <TextField
          label={S.activity.subject}
          value={form.title}
          onChange={set('title')}
          required
        />

        <div className="flex flex-col gap-1">
          <TextField
            label={S.activity.contact}
            hint={S.activity.contactHint}
            placeholder={S.activity.searchContact}
            value={contactSearch}
            onChange={(event) => setContactSearch(event.target.value)}
          />
          <SelectField
            label={S.activity.contact}
            value={form.contactId}
            onChange={set('contactId')}
            className="sr-only-label"
          >
            <option value="">{S.activity.noContact}</option>
            {/* Keep the current selection listed even when the search moved on,
                or editing an activity would silently drop its contact. */}
            {activity?.contact && !contacts.some((c) => c.id === activity.contact.id) && (
              <option value={activity.contact.id}>{activity.contact.displayName}</option>
            )}
            {contacts.map((contact) => (
              <option key={contact.id} value={contact.id}>
                {contact.displayName}
              </option>
            ))}
          </SelectField>
          {hiddenContacts > 0 && (
            <p className="text-xs text-text-muted">
              {S.activity.moreContacts(formatCount(hiddenContacts))}
            </p>
          )}
        </div>

        <CheckboxField
          label={S.activity.allDay}
          checked={form.allDay}
          onChange={set('allDay')}
        />

        {/* Both fields are PICKERS now, and both still accept typing. The
            value each emits is unchanged — an ISO date and `HH:mm` — because
            `isoDateAndTimeToInstant` remains the single place the two are
            combined into the instant the server stores. */}
        <div className="flex gap-3">
          <JalaliDateField
            label={S.activity.date}
            className="flex-1"
            value={form.date}
            onChange={(value) => setForm((current) => ({ ...current, date: value }))}
          />
          {!form.allDay && (
            <fieldset className="flex flex-1 gap-2">
              <legend className="sr-only">{S.activity.time}</legend>
              <TimeField
                label={S.activity.startTime}
                className="flex-1"
                value={form.startTime}
                onChange={(value) => setForm((current) => ({ ...current, startTime: value }))}
              />
              <TimeField
                label={S.activity.endTime}
                className="flex-1"
                value={form.endTime}
                onChange={(value) => setForm((current) => ({ ...current, endTime: value }))}
              />
            </fieldset>
          )}
        </div>

        {/* «مسئول فعالیت» — WHO WILL DO IT, as opposed to «مخاطب» above, which
            is who it is WITH. That collision is what this form was reported
            for, and the labels now say which is which.

            It was a box you typed a UUID into, bound to a form key nothing
            read, so it wrote nothing even when filled in correctly. It is a
            picker over the SAME `USERS_QUERY` the calendar's user switcher
            already uses — no second user-selector.

            ADMIN only, because assigning to somebody else is ADMIN at the BFF
            and again in the scheduling service. A USER sees the field they
            cannot change as a plain sentence rather than a control that would
            be refused. */}
        {isAdmin ? (
          <SelectField
            label={S.activity.assignee}
            hint={S.activity.assigneeHint}
            value={form.assignedTo}
            onChange={set('assignedTo')}
          >
            {/* Empty is not "nobody": the service reads it as the caller. */}
            <option value="">{S.activity.assigneeSelf}</option>
            {users.map((user) => (
              <option key={user.id} value={user.id}>
                {user.displayName}
              </option>
            ))}
          </SelectField>
        ) : (
          <p className="text-sm text-text-muted">
            {S.activity.assignee}: {me?.displayName ?? S.activity.assigneeSelf}
          </p>
        )}

        <TextArea
          label={S.activity.description}
          rows={3}
          value={form.description}
          onChange={set('description')}
        />

        <fieldset className="flex flex-col gap-2">
          <legend className="text-sm font-medium">{S.activity.reminders}</legend>
          {form.reminders.length === 0 && (
            <p className="text-xs text-text-muted">{S.activity.noReminders}</p>
          )}
          {form.reminders.map((reminder, index) => (
            <div key={`${reminder.offset}-${reminder.channel}`} className="flex items-end gap-2">
              <SelectField
                label={S.activity.reminderOffset}
                value={String(reminder.offset)}
                onChange={(event) =>
                  setForm((current) => ({
                    ...current,
                    reminders: current.reminders.map((r, i) =>
                      i === index ? { ...r, offset: Number(event.target.value) } : r,
                    ),
                  }))
                }
              >
                {OFFSETS.map((offset) => (
                  <option key={offset} value={offset}>
                    {S.activity.reminderOffsets[String(offset)]}
                  </option>
                ))}
              </SelectField>
              <SelectField
                label={S.activity.reminderChannel}
                value={reminder.channel}
                onChange={(event) =>
                  setForm((current) => ({
                    ...current,
                    reminders: current.reminders.map((r, i) =>
                      i === index ? { ...r, channel: event.target.value } : r,
                    ),
                  }))
                }
              >
                {Object.entries(REMINDER_CHANNEL_LABELS).map(([value, label]) => (
                  <option key={value} value={value}>
                    {label}
                  </option>
                ))}
              </SelectField>
              <Button
                type="button"
                variant="ghost"
                aria-label={S.app.close}
                onClick={() =>
                  setForm((current) => ({
                    ...current,
                    reminders: current.reminders.filter((_, i) => i !== index),
                  }))
                }
              >
                <X size={14} aria-hidden="true" />
              </Button>
            </div>
          ))}
          <Button
            type="button"
            onClick={() =>
              setForm((current) => ({
                ...current,
                reminders: [...current.reminders, { offset: 30, channel: 'IN_APP' }],
              }))
            }
          >
            <Plus size={14} aria-hidden="true" />
            {S.activity.addReminder}
          </Button>
        </fieldset>

        {/* «فایل‌های پیوست». Hidden on the `onCreate` path (accepting a
            follow-up suggestion), which returns the suggestion rather than the
            activity — there would be no owner id to upload against, and a
            picker that silently dropped the file would be worse than none. */}
        {!onCreate && (
          <fieldset className="flex flex-col gap-2">
            <legend className="text-sm font-medium">{S.activity.attachments}</legend>

            {/* Already stored, on an activity being edited. */}
            {stored.map((file) => (
              <button
                key={file.id}
                type="button"
                onClick={() => openAttachment(file.id)}
                title={`${file.fileName} — ${formatBytes(file.sizeBytes)}`}
                className="flex items-center gap-2 self-start rounded border border-border
                           px-3 py-1 text-sm text-text-muted hover:bg-surface-hover
                           hover:text-text"
              >
                <Paperclip size={13} aria-hidden="true" />
                {file.fileName}
              </button>
            ))}

            {/* Chosen in this session, not yet uploaded. */}
            {files.map((entry) => (
              <div key={entry.key} className="flex items-center gap-2 text-sm">
                <Paperclip size={13} aria-hidden="true" className="text-text-muted" />
                <span className="truncate">{entry.file.name}</span>
                <span className="text-xs text-text-muted">
                  {entry.state === 'uploading'
                    ? `${Math.round(entry.progress * 100)}٪`
                    : formatBytes(entry.file.size)}
                </span>
                {entry.error && <span className="text-xs text-danger">{entry.error}</span>}
                {entry.state !== 'uploading' && (
                  <Button
                    type="button"
                    variant="ghost"
                    aria-label={S.app.remove}
                    onClick={() =>
                      setFiles((current) => current.filter((f) => f.key !== entry.key))
                    }
                  >
                    <X size={13} aria-hidden="true" />
                  </Button>
                )}
              </div>
            ))}

            {stored.length === 0 && files.length === 0 && (
              <p className="text-xs text-text-muted">{S.activity.noFiles}</p>
            )}

            <Button
              type="button"
              className="self-start"
              disabled={uploading}
              onClick={() => fileInputRef.current?.click()}
            >
              <Plus size={14} aria-hidden="true" />
              {S.activity.addFile}
            </Button>
            <input
              ref={fileInputRef}
              type="file"
              multiple
              className="hidden"
              aria-label={S.activity.addFile}
              onChange={(event) => {
                if (event.target.files?.length) addFiles(event.target.files);
                event.target.value = '';
              }}
            />
            {files.length > 0 && !isEdit && (
              // Said out loud, because the dialog stays open a moment longer
              // than the button suggests — the activity has to exist before a
              // file can belong to it.
              <p className="text-xs text-text-muted">{S.activity.filesAfterSave}</p>
            )}
          </fieldset>
        )}

        {isEdit && <ChecklistPanel activityId={activity.id} />}
      </FormDialog>

      <ConfirmDialog
        open={confirmDelete}
        onOpenChange={setConfirmDelete}
        title={S.activity.delete}
        description={S.activity.deleteConfirm}
        confirmLabel={S.activity.delete}
        destructive
        onConfirm={async () => {
          await deleteActivity({ variables: { id: activity.id } });
          setConfirmDelete(false);
          onSaved?.(null);
          onOpenChange(false);
        }}
      />
    </>
  );
}
